import React, { useState, useMemo } from "react";
import { Calendar, Users, Building2, BookOpen } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { ResourceReport } from "./reports/ResourceReport";
import { SiteReport } from "./reports/SiteReport";
import { SiteScheduleReport } from "./reports/SiteScheduleReport";
import { VisitTasksDialog } from "./reports/VisitTasksDialog";
import { getCurrentWeekDates, getNextWeekDates, formatWeekRange, getTasksForVisit } from "./reports/reportHelpers";

const TAB_LABELS = { resources: 'Active Resources', sites: 'Active Sites', schedule: 'Scheduled Coordinators' };

export const ReportsSection = ({ visits, users, sites, studies, tasks }) => {
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [selectedVisitTasks, setSelectedVisitTasks] = useState([]);
  const [isVisitTasksOpen, setIsVisitTasksOpen] = useState(false);
  const [activeReportTab, setActiveReportTab] = useState('resources');

  const currentWeek = useMemo(() => getCurrentWeekDates(), []);
  const nextWeek = useMemo(() => getNextWeekDates(), []);

  const activeUsers = useMemo(
    () => users.filter(u => u.role === 'site_coordinator' && u.sites?.length).sort((a, b) => a.name.localeCompare(b.name)),
    [users]
  );
  const activeSites = useMemo(
    () => sites.filter(s => visits.some(v => v.site_id === s.id)).sort((a, b) => a.name.localeCompare(b.name)),
    [sites, visits]
  );

  const handleVisitClick = (visit) => {
    setSelectedVisit(visit);
    setSelectedVisitTasks(getTasksForVisit(visit, tasks));
    setIsVisitTasksOpen(true);
  };

  const handleSiteGroupClick = ({ site, date, coordinator, visits: siteVisits }) => {
    setSelectedVisit({ site, date: date.toDateString(), coordinator, visits: siteVisits });
    setSelectedVisitTasks(siteVisits.flatMap(v => getTasksForVisit(v, tasks)));
    setIsVisitTasksOpen(true);
  };

  const visitsThisWeek = useMemo(
    () => currentWeek.reduce((tot, d) => {
      const ds = d.toISOString().split('T')[0];
      return tot + visits.filter(v => new Date(v.date).toISOString().split('T')[0] === ds).length;
    }, 0),
    [currentWeek, visits]
  );
  const visitsNextWeek = useMemo(
    () => nextWeek.reduce((tot, d) => {
      const ds = d.toISOString().split('T')[0];
      return tot + visits.filter(v => new Date(v.date).toISOString().split('T')[0] === ds).length;
    }, 0),
    [nextWeek, visits]
  );

  const renderTabContent = () => {
    if (activeReportTab === 'resources') {
      return (
        <>
          <ResourceReport weekDates={currentWeek} weekTitle={`Current Week (${formatWeekRange(currentWeek)})`} activeUsers={activeUsers} visits={visits} users={users} studies={studies} onVisitClick={handleVisitClick} />
          <ResourceReport weekDates={nextWeek} weekTitle={`Upcoming Week (${formatWeekRange(nextWeek)})`} activeUsers={activeUsers} visits={visits} users={users} studies={studies} onVisitClick={handleVisitClick} />
        </>
      );
    }
    if (activeReportTab === 'schedule') {
      return (
        <>
          <SiteScheduleReport weekDates={currentWeek} weekTitle={`Current Week (${formatWeekRange(currentWeek)})`} activeUsers={activeUsers} sites={sites} visits={visits} users={users} onSiteGroupClick={handleSiteGroupClick} />
          <SiteScheduleReport weekDates={nextWeek} weekTitle={`Upcoming Week (${formatWeekRange(nextWeek)})`} activeUsers={activeUsers} sites={sites} visits={visits} users={users} onSiteGroupClick={handleSiteGroupClick} />
        </>
      );
    }
    return (
      <>
        <SiteReport weekDates={currentWeek} weekTitle={`Current Week (${formatWeekRange(currentWeek)})`} activeSites={activeSites} visits={visits} studies={studies} onVisitClick={handleVisitClick} />
        <SiteReport weekDates={nextWeek} weekTitle={`Upcoming Week (${formatWeekRange(nextWeek)})`} activeSites={activeSites} visits={visits} studies={studies} onVisitClick={handleVisitClick} />
      </>
    );
  };

  const tabs = [
    { key: 'resources', label: 'Report by Resources', activeColor: 'text-purple-600' },
    { key: 'sites', label: 'Report by Sites', activeColor: 'text-green-600' },
    { key: 'schedule', label: 'Site Schedule', activeColor: 'text-blue-600' },
  ];

  const summaryTotal = activeReportTab === 'resources' ? activeUsers.length : activeSites.length;

  return (
    <div data-testid="reports-section" className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Weekly Visit Reports</h1>
          <p className="text-gray-600">Resource scheduling and visit overview by week</p>
        </div>
      </div>

      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg" data-testid="report-tabs">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveReportTab(t.key)}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${activeReportTab === t.key ? `bg-white ${t.activeColor} shadow-sm` : 'text-gray-600 hover:text-gray-800'}`}
            data-testid={`report-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {renderTabContent()}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-blue-100">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-blue-900">{TAB_LABELS[activeReportTab]}</CardTitle>
            {activeReportTab === 'sites' ? <Building2 className="h-4 w-4 text-blue-600" /> : <Users className="h-4 w-4 text-blue-600" />}
          </CardHeader>
          <CardContent><div className="text-3xl font-bold text-blue-900">{summaryTotal}</div></CardContent>
        </Card>
        <Card className="border-0 shadow-lg bg-gradient-to-br from-green-50 to-green-100">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-green-900">This Week Visits</CardTitle>
            <Calendar className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent><div className="text-3xl font-bold text-green-900">{visitsThisWeek}</div></CardContent>
        </Card>
        <Card className="border-0 shadow-lg bg-gradient-to-br from-yellow-50 to-yellow-100">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-yellow-900">Next Week Visits</CardTitle>
            <Calendar className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent><div className="text-3xl font-bold text-yellow-900">{visitsNextWeek}</div></CardContent>
        </Card>
        <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-50 to-purple-100">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-purple-900">Active Studies</CardTitle>
            <BookOpen className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent><div className="text-3xl font-bold text-purple-900">{new Set(visits.map(v => v.study_id)).size}</div></CardContent>
        </Card>
      </div>

      <VisitTasksDialog
        open={isVisitTasksOpen}
        onOpenChange={setIsVisitTasksOpen}
        selectedVisit={selectedVisit}
        selectedVisitTasks={selectedVisitTasks}
        studies={studies}
      />
    </div>
  );
};
